<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="(showLoader && !showFilters) || (!tableData.length && !showLoader && !applyErrorState && !showFilters)"
    :showFiltersAndEmptyState="!tableData.length && !showLoader && !applyErrorState && showFilters"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="t('transfers.history.nacional.emptyStates.error')"
    :showFloatingButton="showFilters && !applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    @floatingButtonClicked="showFiltersModal = true"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFilters(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
            <!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <!-- Table Generic -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <!-- Yes, this v-if can be applied directly in component, but for magic reason, if it's there, the transition only triggers the first time -->
            <div v-if="!!tableData.length && !showLoader && !applyErrorState" class="transition-helper-element prevent-auto-margins">
                <table-generic
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getTransfersListHasMore"
                :isLoadingMore="tableLoadingMore"
                @loadMore="loadMoreHandler"
                @rowClicked="rowClickedHandler($event)"
                @moreOptionsClicked="moreOptionsClicked"
                @downloadClicked="downloadClickedHandler"/>
            </div>
        </transition>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="!tableData.length && !showLoader && !applyErrorState"
            :illustration="emptyStateIllustration"
            :title="emptyStateTitle"
            :description="emptyStateDescription"/>
        </template>
	</wrapper-features-templates-one-column>
    <!-- Filters Modal -->
    <filters-modal
    v-if="showFiltersModal"
    :appliedFilters="appliedFilters"
    :statusList="getTransfersStatusList"
    :operationTypesList="operationTypes"
    :channelsList="getChannelsList"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
    @hideMe="showFiltersModal = false"
    @applyFilters="applyFilters" />
    <!-- Detail Modal -->
    <detail-modal
    v-if="showDetailsModal"
    :detailData="selectedTransfer"
    @updateTransferFavoriteStatus="updateFavoriteStatusHandler"
    @hideMe="showDetailsModal = false" />
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal "
    emailAction="sendOperationEmailNotification"
    smsAction="sendOperationSMSNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="sendInfoData"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- ShareLinkModal -->
    <modal-centered-share-link
    v-if="showShareLinkModal"
    :operationId="sendInfoData.operationId"
    @hideMe="showShareLinkModal = false"/>
    <!-- Component to handle download logic-->
    <download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import TableGeneric from '@/components/generic/table/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
import FiltersModal from '@/components/features/transfers/history/nacional/FiltersModal'
import DetailModal from '@/components/features/transfers/history/DetailModal'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import {
    getDate,
    createJSONDateStartDay,
    createJSONDateEndDay,
    getDateWithTime
} from '@/assets/scripts/dateBuilders'
import { getCurrentRouteMenuAdditionalData } from '@/assets/scripts/parsers/menu'
import { amountFormat, currencyParser } from '@/assets/scripts/amounts'
import { operationsIconHandler, operationsClassesHandler } from '@/assets/scripts/mappers/operationsHistory'
import { clearObject } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestTransfersList,
    requestTransfersStatusList,
    requestChannelsList,
    clearFeatureStates,
    updateFavoriteStatus
} = mapActions(moduleMapper.TRANSFERS_HISTORY_NACIONAL)
const {
    getTransfersList,
    getTransfersListHasMore,
    getTransfersStatusList,
    getChannelsList
} = mapGetters(moduleMapper.TRANSFERS_HISTORY_NACIONAL)
const { getMobileAppState } = mapGetters()

/* Generic Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const selectedTransfer = ref(null)
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showShareLinkModal = ref(false)

/*************************************************
*                                                *
*                Pagination Params               *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const paginationParams = ref({})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1
    }
}

/*************************************************
*                                                *
*                  Filters Logic                 *
*                                                *
*************************************************/
const showFilters = ref(false)
const showFiltersModal = ref(false)

const appliedFilters = ref({
    beginDate: null,
    endDate: null,
    operationType: null,
    statusId: null,
    channel: null,
    initialAmount: null,
    finalAmount: null
})

const filterTags = ref([])

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('transfers.history.nacional.filters.beginDate'),
        placeholder: t('transfers.history.nacional.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('transfers.history.nacional.filters.endDate'),
        placeholder: t('transfers.history.nacional.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

const applyFilters = (filters) => {
    appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`transfers.history.nacional.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

    resetPaginationParams()
    nationalTransfersRequest()
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    nationalTransfersRequest()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    nationalTransfersRequest()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
        case 'operationType':
			return operationTypes.value.find(type => type.id === value).name
        case 'statusId':
            return getTransfersStatusList.value.find(status => status.id === value).description
        case 'channel':
            return getChannelsList.value.find(channel => channel.id === value).description
        case 'initialAmount':
		case 'finalAmount':
			return amountFormat(value)
		default: return value
	}
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const operationTypes = ref(getCurrentRouteMenuAdditionalData().listOperationIds)

const initialRequests = async () => {
    resetPaginationParams()

    showFilters.value = false
    showLoader.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false
    try {
        await requestTransfersStatusList()
        await requestChannelsList()
        await nationalTransfersRequest()

        if (getTransfersList.value.length) showFilters.value = true

        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, initialRequests)
    }
}

const nationalTransfersRequest = async () => {
    cancelAllPendingRequests()

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    const payload = {
        ...paginationParams.value,
        beginDate: createJSONDateStartDay(appliedFilters.value.beginDate),
        endDate: createJSONDateEndDay(appliedFilters.value.endDate),
        statusId: appliedFilters.value.statusId,
        operationType: appliedFilters.value.operationType ?? operationTypes.value.map(type => type.id),
        channel: appliedFilters.value.channel,
        initialAmount: appliedFilters.value.initialAmount,
        finalAmount: appliedFilters.value.finalAmount
    }

    try {
        await requestTransfersList(payload)

        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        if (paginationParams.value.pageNumber === 1) applyErrorState.value = true
		errorHandler(error.response.data, nationalTransfersRequest)
    }
}

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    nationalTransfersRequest()
}

/*************************************************
*                                                *
*             National Transfers Data            *
*                                                *
*************************************************/
const sendInfoData = ref({ operationId: '' })
const downloadLink = ref(null)
const showDetailsModal = ref(false)

const tableHeaderData = [
{
        label: t('transfers.history.nacional.tableHeaders.type'),
        rowCellType: 'description',
        showInMobile: { groupWith: ['amount', 'date'] }
    },
    { label: t('transfers.history.nacional.tableHeaders.amount'), rowCellType: 'amount', dataAttr: 'amount' },
    { label: t('transfers.history.nacional.tableHeaders.date'), dataAttr: 'date' },
    { label: t('transfers.history.nacional.tableHeaders.status'), rowCellType: 'status', dataAttr: 'statusDescription', showInMobile: true },
    { dataAttr: 'download', rowCellType: 'download' },
    { dataAttr: 'tableOptions', rowCellType: 'more-options', showInMobile: true }
]

const tableStyles = [
    { width: '230px', 'min-width': '230px' },
    { 'text-align': 'end' },
    { width: '130px', 'min-width': '130px' },
    null,
    null,
    null
]

const tableMoreOptions = () => {
    const moreOptions = [
        {
            id: 1,
            text: t('transfers.history.nacional.moreOptions.sendEmail'),
            icon: 'letter',
            callbackFunction: () => { showSendEmailInfoModal.value = true }
        }, {
            id: 2,
            text: t('transfers.history.nacional.moreOptions.sendSms'),
            icon: 'chat',
            callbackFunction: () => { showSendSMSInfoModal.value = true }
        }, {
            id: 3,
            text: t('transfers.history.nacional.moreOptions.generateLink'),
            icon: 'link',
            callbackFunction: () => { showShareLinkModal.value = true }
        }
    ]

    if (getMobileAppState.value) {
        moreOptions.unshift({
            id: 4,
            text: t('transfers.history.nacional.moreOptions.download'),
            icon: 'download',
            callbackFunction: (rowId) => { downloadClickedHandler(rowId) }
        })
    }
    return moreOptions
}

const tableData = computed(() => {
    return getTransfersList.value.map(transfer => ([
        { value: transfer.id, dataAttr: 'id' },
        { type: 'title', value: transfer.operationName, dataAttr: 'description' },
        { type: 'amount', value: { content: -transfer.amount, currency: currencyParser(transfer.currency), hasArrowIcons: true }, dataAttr: 'amount' },
        {
            type: 'description',
            value: {
                content: getDateWithTime(transfer.operationDate),
                classes: 'capitalize'
            },
            dataAttr: 'date'
        },
        {
            type: 'status',
            value: {
                text: transfer.state.description,
                icon: operationsIconHandler(transfer.state.id),
                classes: operationsClassesHandler(transfer.state.id)
            },
            dataAttr: 'status'
        },
        { type: 'download', dataAttr: 'download', hidden: !transfer.hasReceipt },
        { type: 'more-options', value: tableMoreOptions(), dataAttr: 'tableOptions', hidden: !transfer.hasReceipt }
    ]))
})

const rowClickedHandler = row => {
    setDetailData(row)

    showDetailsModal.value = true
}

const setDetailData = row => {
    const isFavorite = getTransfersList.value.find(transfer => transfer.id === row.rowId).favorite
    const isFavoritable = getTransfersList.value.find(transfer => transfer.id === row.rowId).favoritable
    const statusCode = getTransfersList.value.find(transfer => transfer.id === row.rowId).state.id

    selectedTransfer.value = {
        id: row.rowId,
        title: row.rowData.find(data => data.type === 'title').value,
        amount: row.rowData.find(data => data.type === 'amount').value,
        status: {
            ...row.rowData.find(data => data.type === 'status').value,
            color: operationsClassesHandler(statusCode)
        },
        hasOptions: row.rowData.find(data => data.type === 'more-options') ? !row.rowData.find(data => data.type === 'more-options').hidden : false,
        favorite: isFavorite,
        favoritable: isFavoritable,
        requestAction: `${moduleMapper.TRANSFERS_HISTORY_NACIONAL}/requestOperationDetails`
    }
}

const moreOptionsClicked = row => {
    sendInfoData.value = {
        operationId: row.rowId
    }

	tableMoreOptions().find(obj => obj.id === row.optionClicked).callbackFunction(row.rowId)
}

const downloadClickedHandler = rowId => {
    downloadLink.value = createDownloadGenericReceiptPdf({
        params: {
            operationID: rowId
        }
    })
}

const updateFavoriteStatusHandler = obj => {
    updateFavoriteStatus(obj)

    selectedTransfer.value = {
        ...selectedTransfer.value,
        favorite: getTransfersList.value.find(transfer => transfer.id === obj.favoriteId).favorite,
        favoritable: getTransfersList.value.find(transfer => transfer.id === obj.favoriteId).favoritable
    }
}

/*************************************************
*                                                *
*                  Empty States                  *
*                                                *
*************************************************/
const emptyStateIllustration = computed(() => {
    if (filterTags.value.length !== 0) return 'emptyState/filters'

    return 'emptyState/no-movements'
})

const emptyStateTitle = computed(() => {
    if (filterTags.value.length !== 0) return t('transfers.history.nacional.emptyStates.noResults.noFilteredResultsTitle')

    return t('transfers.history.nacional.emptyStates.noResults.noTransfersTitle')
})

const emptyStateDescription = computed(() => {
    if (filterTags.value.length !== 0) return t('transfers.history.nacional.emptyStates.noResults.noFilteredResultsMessage')

    return t('transfers.history.nacional.emptyStates.noResults.noTransfersDescription')
})

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.input-elements-container {
    width: 202px;
}

.transition-helper-element {
    @include commonEnteringAnimation;
}
</style>
